import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
  Chip, IconButton, Tooltip, Switch,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';

export default function Departments() {
  const [active, setActive] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState({});
  const [form, setForm] = useState({ standard: '', section: '' });

  const load = async () => {
    setLoading(true);
    const [activeData, inactiveData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
      InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Inactive' }),
    ]);
    setActive(activeData?.sections || []);
    setInactive(inactiveData?.sections || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.standard.trim()) { alert('Standard is required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addDeptartmentSection, {
      standard: form.standard.trim(),
      section: form.section.trim(),
      createdBy: '1',
      status: 'Active',
    });
    setSaving(false);
    if (data?.code === '200') {
      setDialog(false);
      setForm({ standard: '', section: '' });
      load();
    } else alert(data?.message || 'Failed to add class');
  };

  const handleToggle = async (item) => {
    const newStatus = item.status === 'Active' ? 'Inactive' : 'Active';
    setToggling(p => ({ ...p, [item.ID]: true }));
    await InvokeApi(Config.main_url + apiList.deleteDepartmentSection, {
      id: item.ID,
      status: newStatus,
    });
    setToggling(p => ({ ...p, [item.ID]: false }));
    load();
  };

  const displayed = showInactive ? [...active, ...inactive] : active;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Classes / Sections</Typography>
          <Typography variant="body2" color="text.secondary">
            {active.length} active · {inactive.length} inactive
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e8eaf6', borderRadius: 2, px: 1.5, py: 0.5 }}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary">Show Inactive</Typography>
            <Switch
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' },
              }}
            />
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Class
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Standard', 'Section', 'Status', 'Active / Inactive'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No classes found
                    </TableCell>
                  </TableRow>
                ) : displayed.map((d, i) => (
                  <TableRow key={d.ID || i} hover sx={{ opacity: d.status === 'Inactive' ? 0.6 : 1 }}>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight={700} fontSize={14} color="#1a237e">{d.standard}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={d.section || '—'} size="small"
                        sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={d.status || 'Active'}
                        size="small"
                        sx={{
                          background: d.status === 'Active' || !d.status ? '#e8f5e9' : '#ffebee',
                          color: d.status === 'Active' || !d.status ? '#2e7d32' : '#c62828',
                          fontWeight: 600, fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {toggling[d.ID] ? (
                        <CircularProgress size={20} sx={{ color: '#1a237e' }} />
                      ) : (
                        <Tooltip title={d.status === 'Active' ? 'Set Inactive' : 'Set Active'}>
                          <Switch
                            checked={d.status === 'Active' || !d.status}
                            onChange={() => handleToggle(d)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2e7d32' },
                              '& .MuiSwitch-switchBase': { color: '#c62828' },
                              '& .MuiSwitch-track': { backgroundColor: '#ffcdd2' },
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Class / Section
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Standard *" value={form.standard}
                onChange={e => setForm(p => ({ ...p, standard: e.target.value }))}
                placeholder="e.g. Nursery, Junior KG, Senior KG" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Section" value={form.section}
                onChange={e => setForm(p => ({ ...p, section: e.target.value }))}
                placeholder="e.g. A, B, C"
                onKeyDown={e => e.key === 'Enter' && handleSave()} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
